/**
 * Rooms API (#790, #784, #914, #1098, #1216)
 *
 * Chat-room domain module: the `Room` shape, the date-based category logic,
 * and the club-rooms fetch. Extracted from `hooks/useRooms.ts` for #2066
 * Slice B so native can consume the rooms surface per the mobile contract
 * (§1: api logic lives in `lib/api/`; native hooks wrap domain modules).
 * The web hook re-exports these names, so existing imports are unchanged.
 */

import { apiGet } from '../api';

// Training group status type
export type TrainingGroupStatus = 'upcoming' | 'active' | 'completed' | 'archived';

// Room interface matching backend schema (Issue #790, #784, #914, #1098, #1216)
export interface Room {
  id: string;
  club_id: string;
  room_type: 'club_lobby' | 'event_room' | 'training_group_room' | 'series_room';
  owner_id: string;
  access_policy: 'club_members' | 'event_rsvp' | 'group_members' | 'series_rsvp';
  last_message_at: number | null;
  created_at: number;
  // Leaders-only flag (#1098)
  is_leaders_only?: boolean;
  // Computed display fields (from backend JOIN queries)
  display_name: string;
  // Write permission (#784: club members can read, but only group members can write)
  can_write: boolean;
  // Announce permission (#1445/#2759): true for leaders/owners (announcements
  // are free for every club — tier no longer gates them), and ONLY on the true
  // club lobby (`room_type === 'club_lobby' && !is_leaders_only`) where can_write
  // is also true. The lobby-scope clause is load-bearing — a leader can_write in the
  // leaders/event/series rooms too, so a client must NOT derive the toggle off
  // can_write alone or it would render an affordance the send handler 403s.
  // Drives the composer announcement toggle. Optional for backward compat with
  // pre-#1445 cached/mocked room payloads.
  can_announce?: boolean;
  // #2708 pin capability hint (Core Rule 48 dual-site — this is the HINT; the
  // enforcement gate lives in the PATCH/DELETE /rooms/:roomId/pin handlers).
  // UNLIKE can_announce, pin is NOT lobby-gated — a leader/owner may pin in any
  // room they can write, so the server derives this as `can_write && leader/
  // owner && archived_at === null` (the archived_at term is load-bearing:
  // requireRoomAccess selects `archived_at IS NULL`, so a pin on a grace-window
  // archived room 404s — the hint must agree). Drives the Pin/Unpin sheet row.
  // Optional for backward compat with pre-#2708 cached/mocked room payloads.
  can_pin?: boolean;
  // Event date fields (#914: for date-based categorization)
  event_starts_at?: number | null;
  event_ends_at?: number | null;
  // Training group date fields (#914)
  training_group_start_date?: number | null;
  training_group_end_date?: number | null;
  training_group_status?: TrainingGroupStatus | null;
  // Series date fields (#1216: for recurring event series rooms)
  series_starts_on?: number | null;
  series_next_event?: number | null;
}

// Room category for date-based grouping (#914)
export type RoomCategory = 'lobby' | 'active' | 'upcoming' | 'archive';

/**
 * Determine the category for a room based on event/training group dates (#914, #1216)
 */
export function getRoomCategory(room: Room, now: number = Math.floor(Date.now() / 1000)): RoomCategory {
  // Lobby is always in its own category
  if (room.room_type === 'club_lobby') {
    return 'lobby';
  }

  // Event rooms: categorize by event start/end times
  if (room.room_type === 'event_room') {
    const starts = room.event_starts_at;
    const ends = room.event_ends_at;

    if (!starts) return 'archive'; // No start time = treat as archive

    // Active: event is happening now (starts_at <= now <= ends_at)
    // For events without end time, consider active if same day
    if (ends) {
      if (starts <= now && now <= ends) return 'active';
      if (now < starts) return 'upcoming';
      return 'archive'; // now > ends
    } else {
      // No end time: active if started today, otherwise based on starts_at
      const startOfDay = new Date(starts * 1000);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(startOfDay);
      endOfDay.setDate(endOfDay.getDate() + 1);

      const startDayTs = Math.floor(startOfDay.getTime() / 1000);
      const endDayTs = Math.floor(endOfDay.getTime() / 1000);

      if (now >= startDayTs && now < endDayTs) return 'active';
      if (now < starts) return 'upcoming';
      return 'archive';
    }
  }

  // Training group rooms: categorize by dates and status
  if (room.room_type === 'training_group_room') {
    const status = room.training_group_status;
    const startDate = room.training_group_start_date;
    const endDate = room.training_group_end_date;

    // Prefer explicit status if set
    if (status === 'active') return 'active';
    if (status === 'upcoming') return 'upcoming';
    if (status === 'completed' || status === 'archived') return 'archive';

    // Fall back to date-based logic
    if (startDate && endDate) {
      if (startDate <= now && now <= endDate) return 'active';
      if (now < startDate) return 'upcoming';
      return 'archive';
    }

    // No dates = archive
    return 'archive';
  }

  // Series rooms (#1216): categorize by next event time
  if (room.room_type === 'series_room') {
    const nextEvent = room.series_next_event;

    if (!nextEvent) return 'archive'; // No upcoming events

    // Active if next event starts within 1 hour
    if (nextEvent <= now + 3600) return 'active';
    return 'upcoming';
  }

  return 'archive';
}

/**
 * Copy for the read-only banner that replaces the chat composer when a room's
 * `can_write` is false (#784 training-group rooms, #2479 event/series rooms).
 *
 * Named per room type on purpose: a single generic string is what created the
 * #2479 complaint in the first place ("Join this group to post messages" shown
 * to somebody who was already in the group). The banner must name the blocker
 * that actually applies to the room in front of the user.
 *
 * - `training_group_room` — the blocker is training-group membership (#784).
 * - everything else — the blocker is club membership. Since #2479 dropped the
 *   RSVP gate, club membership is the only remaining write gate on event and
 *   series rooms (backend: `checkRoomAccess` in `middleware/auth.ts` +
 *   the `can_write` CASE in `routes/clubs.ts`).
 *
 * Shared verbatim by web (`ClubChatPage`) and native (`ClubChatScreen`).
 */
export function getReadOnlyBannerCopy(roomType: Room['room_type'] | undefined): string {
  if (roomType === 'training_group_room') {
    return 'Join this training group to post messages';
  }
  return 'You must be a club member to post messages';
}

// API response shape
export interface ClubRoomsResponse {
  rooms: Room[];
}

export const roomsApi = {
  /**
   * Fetch all accessible rooms for a club
   * GET /clubs/:clubId/rooms
   * Returns: Lobby + all events/training groups user has access to
   */
  getClubRooms: (clubId: string) =>
    apiGet<ClubRoomsResponse>(`/clubs/${clubId}/rooms`),
};
